import { useEffect, useState } from 'react';
import { setProjectVocabulary } from '../services/experiments';
import { Button, Modal, Textarea } from './ui';

/**
 * The project's own words — reagents, instruments, sample codes — sent with
 * every spoken note so the transcriber expects them. The biggest single gain
 * in accuracy, as in ENQI.
 */
export default function VocabularyDialog({
  open,
  onClose,
  projectId,
  words,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  words: string[];
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setText(words.join('\n'));
      setError(null);
    }
  }, [open, words]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await setProjectVocabulary(projectId, text.split(/[\n,]/));
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Words to recognise">
      <p className="mb-3 text-sm text-ink-400">
        Names the transcriber should expect in this project — reagents, instruments, sample codes,
        people. One per line. Reader names and field names are included automatically.
      </p>
      <Textarea
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} onClick={() => void save()}>
          Save
        </Button>
      </div>
    </Modal>
  );
}
